import { useState, useEffect, useMemo } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Skeleton from '@mui/material/Skeleton';
import Avatar from '@mui/material/Avatar';
import Chip from '@mui/material/Chip';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined';
import BarChartIcon from '@mui/icons-material/BarChart';
import '@Partials/_ComStyles.scss';
import '@Partials/_SimpleStyles.scss';
import '@Partials/_OverrideStyles.scss';
import './UserTicketBracketCharts.scss';

export default function UserTicketBracketCharts(props) {
    const className = 'ticketDashboards__userTicketBracketCharts';
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        const timer = setTimeout(() => setLoading(false), 200);
        return () => clearTimeout(timer);
    }, [props.kaiziqResolveConnectionAnalytics, props.topReporter]);

    const stats = useMemo(() => {
        const analytics = props.kaiziqResolveConnectionAnalytics || {};
        const top = props.topReporter || {};
        return [
            {
                id: 'reporters',
                label: 'Total Reporters',
                value: analytics.reporters || 0,
                icon: <PeopleOutlineIcon />,
                color: '#1976d2',
                bgGradient: 'linear-gradient(135deg, #e3f2fd, #bbdefb)'
            },
            {
                id: 'avg',
                label: 'Avg Tickets / Reporter',
                value: analytics.averageTickets || 0,
                icon: <ConfirmationNumberOutlinedIcon />,
                color: '#f57c00',
                bgGradient: 'linear-gradient(135deg, #fff3e0, #ffe0b2)'
            },
            {
                id: 'max',
                label: 'Max Tickets Reported',
                value: analytics.maxTickets || 0,
                icon: <TrendingUpIcon />,
                color: '#388e3c',
                bgGradient: 'linear-gradient(135deg, #e8f5e9, #c8e6c9)'
            },
            {
                id: 'top',
                label: 'Top Reporter',
                value: top.fullName || 'N/A',
                subtitle: top.createdByEmail || '',
                tickets: top.ticketCount || 0,
                icon: <EmojiEventsOutlinedIcon />,
                color: '#d32f2f',
                bgGradient: 'linear-gradient(135deg, #ffebee, #ffcdd2)'
            }
        ];
    }, [props.kaiziqResolveConnectionAnalytics, props.topReporter]);

    const getInitials = (name) => {
        if (!name || name === 'N/A') return '?';
        const parts = name.trim().split(' ');
        if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
        return name.substring(0, 2).toUpperCase();
    };

    if (loading) {
        return (
            <Box className={className}>
                <Box className={`${className}__sectionHeader`}>
                    <Skeleton variant="circular" width={32} height={32} />
                    <Box sx={{ flex: 1 }}>
                        <Skeleton variant="text" width="60%" height={18} />
                        <Skeleton variant="text" width="40%" height={14} />
                    </Box>
                </Box>
                {[...Array(4)].map((_, i) => (
                    <Skeleton key={i} variant="rectangular" height={64} style={{ borderRadius: 12 }} />
                ))}
            </Box>
        );
    }

    return (
        <Box className={className}>
            <Box className={`${className}__sectionHeader`}>
                <Box className={`${className}__sectionHeader__icon`}>
                    <BarChartIcon />
                </Box>
                <Box>
                    <Typography className={`${className}__sectionHeader__title`}>
                        Reporter Insights
                    </Typography>
                    <Typography className={`${className}__sectionHeader__subtitle`}>
                        Submission activity overview
                    </Typography>
                </Box>
            </Box>

            {stats.map((stat) => (
                <Paper
                    key={stat.id}
                    className={`${className}__card${stat.id === 'top' ? ` ${className}__card--featured` : ''}`}
                    elevation={0}
                    sx={{
                        '--card-color': stat.color,
                        '--card-bg': stat.bgGradient
                    }}
                >
                    <Box
                        className={`${className}__card__icon`}
                        sx={{ background: stat.bgGradient, color: stat.color }}
                    >
                        {stat.icon}
                    </Box>

                    <Box className={`${className}__card__body`}>
                        <Typography className={`${className}__card__label`}>
                            {stat.label}
                        </Typography>
                        {stat.id === 'top' ? (
                            <Box className={`${className}__card__topReporter`}>
                                <Avatar
                                    className={`${className}__card__avatar`}
                                    sx={{ bgcolor: stat.color }}
                                >
                                    {getInitials(stat.value)}
                                </Avatar>
                                <Box className={`${className}__card__info`}>
                                    <Typography className={`${className}__card__name`}>
                                        {stat.value}
                                    </Typography>
                                    {stat.subtitle && (
                                        <Typography className={`${className}__card__email`}>
                                            {stat.subtitle}
                                        </Typography>
                                    )}
                                </Box>
                                {stat.tickets > 0 && (
                                    <Chip
                                        size="small"
                                        label={`${stat.tickets}`}
                                        className={`${className}__card__chip`}
                                    />
                                )}
                            </Box>
                        ) : (
                            <Typography className={`${className}__card__value`}>
                                {stat.value}
                            </Typography>
                        )}
                    </Box>

                    <Box
                        className={`${className}__card__indicator`}
                        sx={{ backgroundColor: stat.color }}
                    />
                </Paper>
            ))}
        </Box>
    );
}
